import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { formatDate } from "../utils/format";
import { toast } from "sonner";
import { Plus, X, Trash2 } from "lucide-react";

const EVENT_TYPES = [
  { key: "launch", color: "hsl(var(--success))" },
  { key: "review", color: "hsl(var(--primary))" },
  { key: "budget_reset", color: "hsl(var(--warning))" },
  { key: "offer_expiry", color: "hsl(var(--critical))" },
  { key: "end_date", color: "hsl(var(--critical))" },
  { key: "report", color: "hsl(var(--ai))" },
  { key: "follow_up", color: "hsl(220 15% 40%)" },
  { key: "milestone", color: "hsl(150 70% 30%)" },
];
const COLORS = Object.fromEntries(EVENT_TYPES.map((e) => [e.key, e.color]));

const EMPTY = { title: "", event_type: "review", date: "", client_id: "", campaign_id: "", notes: "" };

export default function CalendarPage() {
  const [events, setEvents] = useState([]);
  const [clients, setClients] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [today] = useState(new Date());
  const [month, setMonth] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [form, setForm] = useState(null);

  const load = () => api.get("/calendar").then((r) => setEvents(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    if (!form.title || !form.date) return toast.error("Title and date required");
    const payload = { ...form };
    ["client_id", "campaign_id"].forEach((k) => { if (!payload[k]) delete payload[k]; });
    try {
      if (form.id) await api.patch(`/calendar/${form.id}`, payload);
      else await api.post("/calendar", payload);
      toast.success(form.id ? "Event updated" : "Event created");
      setForm(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const remove = async () => {
    if (!form?.id) return;
    if (!window.confirm("Delete this event?")) return;
    await api.delete(`/calendar/${form.id}`);
    toast.success("Event deleted");
    setForm(null);
    load();
  };

  const firstDay = new Date(month.getFullYear(), month.getMonth(), 1);
  const lastDay = new Date(month.getFullYear(), month.getMonth() + 1, 0);
  const startDay = firstDay.getDay();
  const daysInMonth = lastDay.getDate();

  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const byDate = events.reduce((acc, e) => { (acc[e.date] = acc[e.date] || []).push(e); return acc; }, {});
  const dateKey = (d) => {
    const dt = new Date(month.getFullYear(), month.getMonth(), d);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
  };
  const monthName = month.toLocaleDateString("en-IN", { month: "long", year: "numeric" });

  return (
    <Layout
      title="Calendar & Timeline"
      subtitle={monthName}
      actions={
        <div className="flex items-center gap-2">
          <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} data-testid="cal-prev" className="h-8 w-8 border border-border rounded-sm">‹</button>
          <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} data-testid="cal-next" className="h-8 w-8 border border-border rounded-sm">›</button>
          <button onClick={() => setForm({ ...EMPTY, date: new Date().toISOString().slice(0, 10) })} data-testid="cal-new-event" className="h-8 px-3 bg-primary text-primary-foreground text-xs font-medium rounded-sm flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> New Event</button>
        </div>
      }
    >
      <div className="bg-card border border-border rounded-sm" data-testid="calendar-grid">
        <div className="grid grid-cols-7 border-b border-border">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className="px-3 py-2 label-mono border-r border-border last:border-r-0">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((d, i) => {
            const key = d ? dateKey(d) : null;
            const dayEvents = key ? byDate[key] || [] : [];
            const isToday = d && new Date().toDateString() === new Date(month.getFullYear(), month.getMonth(), d).toDateString();
            return (
              <div
                key={i}
                onClick={() => d && setForm({ ...EMPTY, date: key })}
                className={`min-h-[110px] p-2 border-r border-b border-border last:border-r-0 ${isToday ? "bg-[hsl(var(--primary))]/5" : ""} ${d ? "cursor-pointer hover:bg-secondary/30" : ""}`}
                data-testid={d ? `cal-day-${key}` : undefined}
              >
                {d && (
                  <>
                    <div className={`text-xs font-semibold mb-1 ${isToday ? "text-primary" : "text-muted-foreground"}`}>{d}</div>
                    <div className="space-y-0.5">
                      {dayEvents.slice(0, 3).map((e) => (
                        <div
                          key={e.id}
                          onClick={(ev) => { ev.stopPropagation(); setForm({ ...e }); }}
                          className="text-[10px] leading-tight truncate flex items-center gap-1 hover:text-primary"
                          data-testid={`cal-event-${e.id}`}
                        >
                          <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: COLORS[e.event_type] }} />
                          <span className="truncate">{e.title}</span>
                        </div>
                      ))}
                      {dayEvents.length > 3 && <div className="text-[10px] text-muted-foreground">+{dayEvents.length - 3} more</div>}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-4 text-xs">
        {EVENT_TYPES.map((e) => (
          <div key={e.key} className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ background: e.color }} />
            <span className="text-muted-foreground capitalize">{e.key.replace("_", " ")}</span>
          </div>
        ))}
      </div>

      {form && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setForm(null)}>
          <div className="bg-card border border-border rounded-sm p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="cal-event-modal">
            <div className="flex justify-between items-start mb-4">
              <div className="font-heading text-lg font-medium">{form.id ? "Edit Event" : "New Event"}</div>
              <button onClick={() => setForm(null)}><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              <input placeholder="Title *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full h-10 px-3 border border-border rounded-sm text-sm" data-testid="cal-title-input" />
              <div className="grid grid-cols-2 gap-2">
                <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="h-10 px-3 border border-border rounded-sm text-sm" data-testid="cal-date-input" />
                <select value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })} className="h-10 px-3 border border-border rounded-sm text-sm">
                  {EVENT_TYPES.map((t) => <option key={t.key} value={t.key}>{t.key.replace("_", " ")}</option>)}
                </select>
              </div>
              <select value={form.client_id || ""} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className="w-full h-10 px-3 border border-border rounded-sm text-sm">
                <option value="">No client</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <select value={form.campaign_id || ""} onChange={(e) => setForm({ ...form, campaign_id: e.target.value })} className="w-full h-10 px-3 border border-border rounded-sm text-sm">
                <option value="">No campaign</option>
                {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <textarea placeholder="Notes" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full h-20 px-3 py-2 border border-border rounded-sm text-sm" />
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={save} data-testid="cal-save-btn" className="flex-1 h-10 bg-primary text-primary-foreground text-sm font-medium rounded-sm">{form.id ? "Save" : "Create"}</button>
              {form.id && <button onClick={remove} data-testid="cal-delete-btn" className="h-10 px-3 border border-[hsl(var(--critical))]/30 text-[hsl(var(--critical))] text-sm rounded-sm flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Delete</button>}
              <button onClick={() => setForm(null)} className="flex-1 h-10 border border-border text-sm rounded-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
